import React from 'react';
import { CalendarDaysIcon, UsersIcon, Building2Icon, DumbbellIcon, CreditCardIcon, WalletIcon, WavesIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
type Bilingual = {
  en: string;
  fr: string;
};
type Node = {
  icon: typeof UsersIcon;
  title: Bilingual;
  desc: Bilingual;
  pos: string;
};

// Positions are only applied from the `sm` breakpoint (orbital layout)
const NODES: Node[] = [{
  icon: CalendarDaysIcon,
  title: {
    fr: 'Règles',
    en: 'Rules'
  },
  desc: {
    fr: 'Politiques, préalables, nombre de places',
    en: 'Policies, prerequisites, capacities'
  },
  pos: 'sm:top-0 sm:left-1/2 sm:-translate-x-1/2'
}, {
  icon: UsersIcon,
  title: {
    fr: 'Personnes',
    en: 'People'
  },
  desc: {
    fr: 'Moniteurs, sauveteurs, qualifications',
    en: 'Instructors, lifeguards, qualifications'
  },
  pos: 'sm:top-[19%] sm:left-0'
}, {
  icon: Building2Icon,
  title: {
    fr: 'Plateaux',
    en: 'Facilities'
  },
  desc: {
    fr: 'Salles, glace, piscine, horaires',
    en: 'Rooms, ice, pool, schedules'
  },
  pos: 'sm:top-[19%] sm:right-0'
}, {
  icon: DumbbellIcon,
  title: {
    fr: 'Équipements',
    en: 'Equipment'
  },
  desc: {
    fr: 'Matériel, inventaire, disponibilités',
    en: 'Gear, inventory, availability'
  },
  pos: 'sm:top-[50%] sm:left-0'
}, {
  icon: CreditCardIcon,
  title: {
    fr: 'Accès',
    en: 'Access'
  },
  desc: {
    fr: 'Carte numérique, droits d’accès, installations',
    en: 'Digital Card, access rights, facilities'
  },
  pos: 'sm:top-[50%] sm:right-0'
}, {
  icon: UsersIcon,
  title: {
    fr: 'Participants',
    en: 'Participants'
  },
  desc: {
    fr: 'Âge, niveaux, inscriptions, listes d’attente',
    en: 'Age, levels, registrations, waitlists'
  },
  pos: 'sm:bottom-0 sm:left-[5%]'
}, {
  icon: WalletIcon,
  title: {
    fr: 'Paiements',
    en: 'Payments'
  },
  desc: {
    fr: 'Transactions, remboursements, soldes, reçus',
    en: 'Transactions, refunds, balances, receipts'
  },
  pos: 'sm:bottom-0 sm:right-[5%]'
}];
export function LoisirsOrchestrator() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bilingual) => isFR ? v.fr : v.en;
  const core = <div className="relative z-10 mx-auto w-[168px] h-[168px] rounded-full bg-white border border-[#E6E6F7] shadow-[0_0_0_14px_rgba(79,70,229,0.05),0_18px_40px_rgba(79,70,229,0.12)] flex flex-col items-center justify-center text-center" data-id="element-4421" data-mp="3e4zwak2egzta">
      <span className="w-9 h-9 rounded-lg bg-[#F1F1FD] flex items-center justify-center" data-id="element-4422" data-mp="3ntq5afvuxo1s">
        <WavesIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-4423" data-mp="3st347z006lkq" />
      </span>
      <span className="font-sans text-[19px] font-semibold tracking-[-0.02em] text-[#121212] leading-tight mt-1.5" data-id="element-4424" data-mp="1nbciv6kv0vt">{isFR ? 'Loisirs ATNA' : 'ATNA Recreation'}</span>
      <span className="font-sans text-[12px] text-[#5B6478] leading-[1.3] mt-1" data-id="element-4425" data-mp="23exzmlncmbn0">
        {isFR ? <>Comprend<br data-id="element-4426" data-mp="27g9p8r682wv7" />Vérifie<br data-id="element-4427" data-mp="27frlzal57ids" />Recommande<br data-id="element-4428" data-mp="27hktst7hs8nd" />Orchestre<br data-id="element-4429" data-mp="27h1nk9r7f8p6" />Agit</> : <>Understands<br data-id="element-4430" data-mp="1zwqqnckhwv1i" />Verifies<br data-id="element-4431" data-mp="1zxk2ut284cr1" />Recommends<br data-id="element-4432" data-mp="1zy24y6bkhl6v" />Orchestrates<br data-id="element-4433" data-mp="1zylcct3lcr6n" />Acts</>}
      </span>
    </div>;
  return <div aria-label={isFR ? 'Dépendances comprises par Loisirs ATNA' : 'Dependencies understood by ATNA Recreation'} data-id="element-4434" data-mp="1v61z5x8tfy2d">
      {/* Orbital layout (sm and up) */}
      <div className="relative hidden sm:block h-[470px]" data-id="element-4435" data-mp="1q4urdy6oayvp">
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" data-id="element-4436" data-mp="1m6x0mqx984q7">
          <ellipse cx="50" cy="52" rx="36" ry="40" fill="none" stroke="#CFD1EC" strokeWidth="1" strokeDasharray="4 5" vectorEffect="non-scaling-stroke" data-id="element-4437" data-mp="2iv39xr54flat" />
        </svg>
        <div className="absolute left-1/2 top-[52%] -translate-x-1/2 -translate-y-1/2" data-id="element-4438" data-mp="24vlhn1sm9pwp">{core}</div>
        {NODES.map(n => <NodeCard key={n.title.en} node={n} pick={pick} className={`absolute w-[178px] ${n.pos}`} data-id="element-4439" data-mp="ydoo58vnol27" />)}
      </div>

      {/* Stacked layout (mobile) */}
      <div className="sm:hidden" data-id="element-4440" data-mp="2hcuqftieq7nf">
        {core}
        <ul className="mt-6 grid grid-cols-1 gap-3" data-id="element-4441" data-mp="2m4crehkfk88e">
          {NODES.map(n => <li key={n.title.en} data-id="element-4442" data-mp="2m67dhz7fu6e2">
              <NodeCard node={n} pick={pick} data-id="element-4443" data-mp="28dr48gny1c4p" />
            </li>)}
        </ul>
      </div>
    </div>;
}
function NodeCard({
  node,
  pick,
  className = ''
}: {
  node: Node;
  pick: (v: Bilingual) => string;
  className?: string;
}) {
  const Icon = node.icon;
  return <div className={`z-10 flex items-start gap-2.5 rounded-xl border border-[#EEF0F4] bg-white p-2.5 shadow-[0_6px_18px_rgba(18,18,40,0.05)] ${className}`} data-id="element-4444" data-mp="352yozmgqnh7k">
      <span className="shrink-0 w-9 h-9 rounded-lg bg-[#F1F1FD] flex items-center justify-center" data-id="element-4445" data-mp="2r5jva5jgcn9s">
        <Icon className="w-[18px] h-[18px] text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-4446" data-mp="2w4n1htb64zjc" />
      </span>
      <span className="min-w-0" data-id="element-4447" data-mp="1fqn5gtfg1u1d">
        <span className="block font-sans text-[13px] font-semibold text-[#121212] leading-tight" data-id="element-4448" data-mp="1knnd38rxz4ul">{pick(node.title)}</span>
        <span className="block font-sans text-[11px] text-[#5B6478] leading-snug mt-0.5" data-id="element-4449" data-mp="1c51sy8s6xy6u">{pick(node.desc)}</span>
      </span>
    </div>;
}